'use client';

import { type FC, type ReactNode, useMemo } from 'react';
import { ScreenContent } from '../../../screen';
import {
  isPartySnapshot, isOpenSnapshot, isBuyingSnapshot, isPlayingSnapshot, isOverSnapshot, isRoundSnapshot,
} from '@cs-shot/battle-sim';
import { LOADING_PHRASES } from './RushMid.translations';
import { RushMidLiveBattle } from './RushMidLiveBattle';
import { GamesHomeScreen } from './GamesHomeScreen';
import { FighterTab } from './FighterTab';
import { BuyScreen } from './BuyScreen';
import { ResultScreen } from './ResultScreen';
import { RushMidLoadingScreen } from './RushMidLoadingScreen';
import { RushMidErrorScreen } from './RushMidErrorScreen';
import { OpenGameScreen, type OpenGameView } from './OpenGameScreen';
import { HistoryScreen } from './HistoryScreen';
import { RatingScreen } from './RatingScreen';
import { RaffleTab } from '../raffle/RaffleTab';
import { RushMidPartyProvider, type RushMidPartyValue } from './RushMidPartyContext';
import { snapshotToRoundUpdate } from './useRushMidMatch';
import { useRushMidController } from './useRushMidController';
import { useRushMidConfig } from '../../../queries/useRushMidConfig';
import { useProfile } from '../../../queries/useProfileQueries';
import { useGameShare } from '../../../hooks/useGameShare';
import { useLocalTranslation } from '../../../hooks/useLocalTranslation';
import translations from './RushMid.translations';

export type { RushMidTab } from './useRushMidController';

/**
 * Rush Mid screen. All state/effects/commands live in {@link useRushMidController};
 * this component only routes the current snapshot/tab to a view. The branches are an
 * ordered resolver (first match wins) — precedence: loading → battle → error → OPEN →
 * result → buy → fighter/history/rating tabs → games home.
 */
export const RushMidGame: FC<{ className?: string; initialTab?: string; initialCreate?: boolean; initialTicket?: number }> = ({ initialTab, initialCreate, initialTicket }) => {
  // launch gate: a teased mode never opens — not via a deep link, not via a stale
  // navigation entry. The tile/menu are already hidden; this covers direct URLs.
  const cfg = useRushMidConfig();
  const tGate = useLocalTranslation(translations);
  const c = useRushMidController(initialTab, initialCreate);
  // your own userId highlights your seat in the slot grid; the invite shares the current game's
  // deep link. Both hooks run every render (before the comingSoon early return) to keep order stable.
  const profileId = useProfile().data?.id;
  const invite = useGameShare(c.matchId, tGate.t('gameShareText'), tGate.t('gameShareButton'));
  // Memoize the party provider value so consumers (games home + squad strip) keep a stable
  // reference across the RushMidGame re-renders that don't touch the party snapshot — a fresh
  // inline object each render would churn every consumer (and re-seed the create sheet).
  const partyValue = useMemo<RushMidPartyValue | null>(() => {
    const s = c.snapshot;
    if (!s || !isPartySnapshot(s)) return null;
    return {
      code: s.code,
      isLeader: s.isLeader,
      leaderUserId: s.leaderUserId,
      youUserId: s.youUserId,
      members: s.members,
      mode: s.mode,
      ticket: s.ticket,
      side: s.side,
      matchMode: s.matchMode,
      allowedModes: s.allowedModes,
      onConfig: c.partyConfig,
      onReady: c.partyReady,
      onLeave: c.partyLeave,
      onKick: c.partyKick,
      onShare: c.partyShare.share,
      sharing: c.partyShare.isSharing,
    };
  }, [c.snapshot, c.partyConfig, c.partyReady, c.partyLeave, c.partyKick, c.partyShare.share, c.partyShare.isSharing]);
  if (cfg.comingSoon) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-8 text-center">
        <span className="rounded-full border border-gold/40 bg-gold/10 px-4 py-1 text-sm font-bold text-gold">
          {tGate.t('comingSoonBadge')}
        </span>
        <div className="text-3xl font-extrabold text-white">???</div>
        <div className="text-sm text-label-secondary">{tGate.t('comingSoonHint')}</div>
      </div>
    );
  }
  const { t, lang, snapshot, matchId, finished } = c;

  const screen = (): ReactNode => {
    if (!c.assetsReady) {
      const phrases = LOADING_PHRASES[lang];
      const phrase = c.loadError ? t('loadingGame') : phrases[c.phraseIdx % phrases.length];
      return <RushMidLoadingScreen phrase={phrase} pct={Math.round(c.loadProgress * 100)} error={c.loadError ? t('errorTitle') : null} />;
    }

    // PLAYING — and OVER until the final round has animated — render the live battle.
    // This wins over the error screen: a stray command failure (e.g. a tapped party invite
    // rejected with "you're in a match") must never unmount a live battle — its "back to
    // lobby" button would dismiss the match and forfeit the stake.
    if (snapshot && isRoundSnapshot(snapshot) && (isPlayingSnapshot(snapshot) || !finished)) {
      return (
        <ScreenContent hideLegalSection scrollKey="rush-mid-battle">
          <div className="absolute inset-0 bg-black">
            {/* key: the battle engine's playback state is per-mount and reschedules on round
                number only — a cross-match snapshot swap must remount it, or the old match's
                scene keeps animating whenever round numbers collide */}
            <RushMidLiveBattle key={matchId} update={snapshotToRoundUpdate(snapshot)} onAction={c.onAction} onFinished={c.finish} />
          </div>
        </ScreenContent>
      );
    }

    if (c.runtimeError) {
      return (
        <RushMidErrorScreen
          message={c.runtimeError}
          onBack={c.again}
          onTopUp={c.runtimeErrorCode === 'INSUFFICIENT_RESOURCES' ? c.topUp : undefined}
        />
      );
    }

    // OPEN — seated in a waiting game: the participant slot grid (leave + invite).
    if (snapshot && isOpenSnapshot(snapshot)) {
      const view: OpenGameView = {
        matchId: snapshot.matchId,
        ticket: snapshot.ticket,
        mode: snapshot.mode,
        map: snapshot.map,
        seats: snapshot.seats,
        botFillAt: snapshot.botFillAt,
        yourSide: snapshot.yourSide,
      };
      // a seated party MEMBER exits by leaving the party (frees just their seat); a solo or the
      // party leader takes the whole game out. The server tags the caller's role on the snapshot.
      const isMember = snapshot.partyRole === 'member';
      return (
        <OpenGameScreen
          game={view}
          youUserId={profileId}
          onLeave={isMember ? c.partyLeave : c.leaveGame}
          leaveVariant={isMember ? 'party' : 'game'}
          onInvite={invite.share}
          inviting={invite.isSharing}
        />
      );
    }

    // OVER: after the battle animates its final round, land directly on the result.
    if (snapshot && isOverSnapshot(snapshot) && snapshot.payout) {
      return (
        <ResultScreen
          payout={snapshot.payout}
          scoreboard={snapshot.scoreboard}
          youFighterId={snapshot.yourFighterId}
          matchId={matchId}
          onAgain={c.again}
          againBusy={c.leavingResult}
          celebrateDrops
          // практика с ботами не пишет match-row — деталей/лога у неё нет (иначе вечный 404-лоадер)
          onViewLog={matchId && snapshot.matchMode !== 'bots' ? () => c.openMatch(matchId, 'log') : undefined}
        />
      );
    }

    if (snapshot && isBuyingSnapshot(snapshot)) {
      return <BuyScreen side={snapshot.side} teammates={snapshot.allies} enemies={snapshot.enemies} buyDeadline={snapshot.buyDeadline} onReady={c.ready} onSave={c.saveLoadout} />;
    }

    if (c.tab === 'fighter') return <FighterTab tab={c.tab} onTab={c.switchTab} />;
    if (c.tab === 'history') return <HistoryScreen tab="history" onTab={c.switchTab} onOpen={c.openMatch} />;
    if (c.tab === 'rating') return <RatingScreen tab="rating" onTab={c.switchTab} />;
    if (c.tab === 'raffle') return <RaffleTab tab={c.tab} onTab={c.switchTab} onRequestCreate={c.requestCreate} />;

    // «Игры» is home for solo AND party — a party's config/roster ride the provider so the home
    // CTA reads the party ready-state. NONE provides null (a plain lobby).
    // A game deep-link / card tap pushes its own stack screen (rush-mid-open-game — see
    // useRushMidController.viewGame) so back/edge-swipe are native.
    return (
      <RushMidPartyProvider value={partyValue}>
        <GamesHomeScreen
          tab={c.tab}
          onTab={c.switchTab}
          onOpen={c.viewGame}
          onSpectate={c.openSpectate}
          onCreate={c.create}
          onCreateParty={c.createParty}
          onJoinParty={c.joinPartyByCode}
          pendingCreate={c.pendingCreate}
          onConsumePendingCreate={c.consumePendingCreate}
          createTicket={initialTicket}
        />
      </RushMidPartyProvider>
    );
  };

  return <>{screen()}</>;
};

export default RushMidGame;
